import React, { useState } from 'react';
import { Modal, Tag, Input, Button, message, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { addJobTag, removeJobTag } from '../../api/jobs';
import { Job } from '../../types';
import styled from 'styled-components';

const TagWrapper = styled.div`
  margin-bottom: 16px;
`;

const TagInputWrapper = styled(Space)`
  margin-top: 16px;
`;

interface ManageTagsModalProps {
    job: Job | null;
    isVisible: boolean;
    onClose: () => void;
    onTagsUpdate: (updatedJob: Job) => void; // Callback to update job state in parent
}

const ManageTagsModal: React.FC<ManageTagsModalProps> = ({
    job,
    isVisible,
    onClose,
    onTagsUpdate,
}) => {
    const { t } = useTranslation();
    const [newTag, setNewTag] = useState<string>('');
    const [loadingTag, setLoadingTag] = useState<string | null>(null); // Store the tag being added/removed

    if (!job) return null; // Don't render if no job data

    const handleAddTag = async () => {
        if (!newTag.trim()) return;
        setLoadingTag(newTag); // Set loading state for this tag
        try {
            const response = await addJobTag(job.id, newTag);
            message.success(t('TAG_ADDED_SUCCESS', { tag: newTag }));
            setNewTag('');
            onTagsUpdate(response.data); // Update parent component state
        } catch (error) {
            message.error(t('ERROR_ADDING_TAG', { tag: newTag }));
            console.error('Error adding tag:', error);
        } finally {
            setLoadingTag(null);
        }
    };

    const handleRemoveTag = async (tagToRemove: string) => {
        setLoadingTag(tagToRemove); // Set loading state for this tag
        try {
            const response = await removeJobTag(job.id, tagToRemove);
            message.success(t('TAG_REMOVED_SUCCESS', { tag: tagToRemove }));
            onTagsUpdate(response.data); // Update parent component state
        } catch (error) {
            message.error(t('ERROR_REMOVING_TAG', { tag: tagToRemove }));
            console.error('Error removing tag:', error);
        } finally {
            setLoadingTag(null);
        }
    };

    return (
        <Modal
            title={t('MANAGE_TAGS_MODAL_TITLE')}
            visible={isVisible}
            onCancel={onClose}
            footer={[
                <Button key="close" onClick={onClose}>
                    {t('CLOSE')}
                </Button>,
            ]}
            width={500}
        >
            <TagWrapper>
                {job.tags && job.tags.length > 0 ? (
                    job.tags.map((tag) => (
                        <Tag
                            key={tag}
                            closable
                            onClose={(e) => {
                                e.preventDefault(); // Prevent default close behavior
                                handleRemoveTag(tag);
                            }}
                            style={{ marginBottom: '8px' }} // Add margin between tags
                            visible={loadingTag !== tag} // Hide tag while loading its removal
                        >
                            {loadingTag === tag ? t('REMOVING...') : tag}
                        </Tag>
                    ))
                ) : (
                    <p>{t('NO_TAGS_ASSIGNED')}</p>
                )}
            </TagWrapper>
            <TagInputWrapper>
                <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder={t('ADD_NEW_TAG_PLACEHOLDER')}
                    onPressEnter={handleAddTag} // Allow adding tag with Enter key
                    disabled={!!loadingTag} // Disable input while any tag operation is loading
                />
                <Button
                    type="primary"
                    onClick={handleAddTag}
                    loading={loadingTag === newTag} // Show loading only if this tag is being added
                    disabled={!newTag.trim() || !!loadingTag} // Disable if input is empty or any tag operation is loading
                >
                    {t('ADD_TAG_BTN')}
                </Button>
            </TagInputWrapper>
        </Modal>
    );
};

export default ManageTagsModal; 